import React, { useEffect, useState } from "react";
import {
    getQuestions,
    patchQuestionOrder,
    deleteQuestion,
} from "@/api/questionnaireQuestions";
import { Button } from "@/components/ui/button";
import { CornerDownRight, GripVertical, Pencil, Plus, Trash2, ChevronDown } from "lucide-react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import QuestionForm from "./QuestionForm";

function SortableRow({ question, onEdit, onAddChild, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition } =
        useSortable({ id: question.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    return (
        <tr
            ref={setNodeRef}
            style={style}
            className={
                "transition border-b " +
                (question.reliesOn ? "bg-[#FDF6FA] text-sm" : "")
            }
        >
            <td
                className={
                    "px-4 py-2 flex items-start gap-2 " +
                    (question.reliesOn ? "pl-12 text-[#8C3161]" : "")
                }
            >
                {!question.reliesOn && (
                    <span {...attributes} {...listeners} className="cursor-grab">
          <GripVertical className="w-4 h-4 text-gray-400 mt-1" />
        </span>
                )}
                {question.reliesOn && (
                    <CornerDownRight className="w-4 h-4 text-[#8C3161] font-bold mt-1" />
                )}
                <span className="truncate max-w-md">{question.questionText}</span>

                {question.questionType === "bool" && !question.reliesOn && (
                    <button
                        onClick={() => onAddChild(question)}
                        className="ml-2 bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1 hover:bg-blue-200 transition"
                    >
                        <ChevronDown className="w-3 h-3" />
                        Add Sub
                    </button>
                )}
            </td>

            <td className="px-4 py-2">
                {question.questionType === "bool" ? "Yes/No" : "Text Field"}
            </td>

            <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                <button
                    onClick={() => onEdit(question)}
                    className="text-sm text-[#5D1F42] hover:underline inline-flex items-center gap-1"
                >
                    <Pencil className="w-4 h-4" />
                    Edit
                </button>

                <button
                    onClick={() => onDelete(question)}
                    className="text-sm text-red-600 hover:underline inline-flex items-center gap-1"
                >
                    <Trash2 className="w-4 h-4" />
                    Delete
                </button>
            </td>
        </tr>
    );

}

export default function QuestionnaireIndex() {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState(null);
    const [dialogTitle, setDialogTitle] = useState("Add Question");

    const fetchQuestions = async () => {
        const data = await getQuestions();
        if (Array.isArray(data)) setQuestions(data);
        setLoading(false);
    };

    useEffect(() => {
        fetchQuestions();
    }, []);

    const handleEdit = (question) => {
        setSelectedQuestion(question);
        setDialogTitle("Edit Question");
        setOpen(true);
    };

    const handleAdd = () => {
        setSelectedQuestion(null);
        setDialogTitle("Add Question");
        setOpen(true);
    };

    const handleAddChild = (parentQuestion) => {
        setSelectedQuestion({ reliesOnId: parentQuestion.id });
        setDialogTitle("Add Sub-Question");
        setOpen(true);
    };

    const handleDelete = async (question) => {
        if (
            confirm(
                `Are you sure you want to delete "${question.questionText}"? This cannot be undone.`
            )
        ) {
            await deleteQuestion(question.id);
            fetchQuestions();
        }
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = questions.findIndex((q) => q.id === active.id);
        const newIndex = questions.findIndex((q) => q.id === over.id);

        const reorderedParents = arrayMove(
            questions.filter((q) => !q.reliesOn),
            oldIndex,
            newIndex
        );

        const payload = reorderedParents.map((q, i) => ({
            id: q.id,
            questionIndex: i + 1,
        }));

        const updatedList = await patchQuestionOrder(payload);
        if (Array.isArray(updatedList)) setQuestions(updatedList);
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-[#5D1F42]">
                    Candidate Questionnaire
                </h2>
                <Button onClick={handleAdd}>
                    <Plus className="w-4 h-4 mr-2" /> Add Question
                </Button>
            </div>

            {loading ? (
                <div>Loading...</div>
            ) : (
                <div className="bg-white shadow rounded-xl overflow-auto">
                    <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext
                            items={questions.map((q) => q.id)}
                            strategy={verticalListSortingStrategy}
                        >
                            <table className="min-w-full border border-gray-200 text-sm">
                                <thead className="bg-gray-50 text-left text-gray-700 uppercase text-xs tracking-wider">
                                <tr>
                                    <th className="px-4 py-3 border-b">Question</th>
                                    <th className="px-4 py-3 border-b">Type</th>
                                    <th className="px-4 py-3 border-b text-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {questions.map((q) => (
                                    <SortableRow
                                        key={q.id}
                                        question={q}
                                        onEdit={handleEdit}
                                        onAddChild={handleAddChild}
                                        onDelete={handleDelete}
                                    />
                                ))}
                                </tbody>
                            </table>
                        </SortableContext>
                    </DndContext>
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>{dialogTitle}</DialogTitle>
                    </DialogHeader>
                    <QuestionForm
                        question={selectedQuestion}
                        allQuestions={questions}
                        onSave={() => {
                            setOpen(false);
                            fetchQuestions();
                        }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
}
